// @flow
import { Input } from 'antd'
import { FormBuilderContext } from 'pages/Contexts/FormBuilderContext'
import * as React from 'react'
import Space from 'ui/atoms/Space'
import Text from 'ui/atoms/Text'
import ToggleField from 'ui/molecules/ToggleField'
import useBuilderQuestion from 'utils/FormBuilder/hooks/useBuilderQuestion'
import useBuilderQuestionOptions from 'utils/FormBuilder/hooks/useBuilderQuestionOptions'

import ErrorLabel from '@/ui/atoms/ErrorLabel'

import AdvancedDateSettings from './AdvancedDateSettings'

function BuilderSideMenuAdvancedSettings(): React.Node {
  const { selectedItem, setQuestion } = React.useContext(FormBuilderContext)

  const question = useBuilderQuestion(selectedItem.id)

  const onChangeQuestionReadOnly = React.useCallback(() => {
    setQuestion(selectedItem.id, {
      ...question,
      is_read_only: !question?.is_read_only,
      is_office_use_only: false,
    })
  }, [question, selectedItem.id, setQuestion])

  const onChangeQuestionIsOfficeUseOnly = React.useCallback(() => {
    setQuestion(selectedItem.id, {
      ...question,
      is_office_use_only: !question?.is_office_use_only,
    })
  }, [question, selectedItem, setQuestion])

  const onUpdateQuestionProps = React.useCallback(
    (newProps) => {
      setQuestion(selectedItem.id, {
        ...question,
        ...newProps,
      })
    },
    [question, selectedItem, setQuestion]
  )

  const questionType =
    typeof question?.question_type === 'object'
      ? question?.question_type?.question_type
      : ''

  const questionSubtype =
    typeof question?.question_type === 'object'
      ? question?.question_type?.question_subtype
      : ''

  const { hasRequiredAndOfficeUse, hasReadOnly } = useBuilderQuestionOptions(
    questionSubtype,
    questionType
  )

  const subtype = question?.question_type?.question_subtype || ''
  const type = question?.question_type?.question_type

  let showEntryRangeLimits = true
  if (
    !['dropdown_multiple', 'vertical_list', 'horizontal_list'].includes(
      subtype
    ) ||
    (type === 'radio' && subtype !== 'dropdown_multiple')
  ) {
    showEntryRangeLimits = false
  }

  let errorMessage
  if (question?.min_value > question?.max_value) {
    errorMessage = 'Minimum value cannot be greater than maximum value'
  }

  return (
    <Space>
      {hasReadOnly && (
        <ToggleField
          horizontal
          primary
          label={
            questionSubtype === 'handout' ? 'Required to View' : 'Read Only'
          }
          onChange={onChangeQuestionReadOnly}
          value={Boolean(question?.is_read_only)}
        />
      )}
      {questionSubtype !== 'handout' && hasRequiredAndOfficeUse && (
        <ToggleField
          horizontal
          primary
          disabled={question?.is_read_only}
          label="Office use"
          onChange={onChangeQuestionIsOfficeUseOnly}
          value={Boolean(question?.is_office_use_only)}
        />
      )}
      {questionType === 'datetime' && <AdvancedDateSettings />}

      {showEntryRangeLimits ? (
        <div className="!mt-3 mb-3 flex flex-col gap-1">
          <Text $semibold>Entry Limits</Text>
          <div className="flex gap-2">
            <div className="flex flex-col items-end">
              <Input
                className="w-full bg-white"
                name="min-value"
                onChange={(e) => {
                  onUpdateQuestionProps({
                    ...question,
                    min_value: e.target.value
                      ? Number(e.target.value)
                      : undefined,
                  })
                }}
                placeholder="Minimum"
                type="number"
                value={question?.min_value}
              />
              <span className="mr-0.5 text-xs text-gray-400/80">Minimum</span>
            </div>
            <div className="flex flex-col items-end">
              <Input
                className="bg-white"
                name="max-value"
                onChange={(e) => {
                  onUpdateQuestionProps({
                    ...question,
                    max_value: e.target.value
                      ? Number(e.target.value)
                      : undefined,
                  })
                }}
                placeholder="Maximum"
                type="number"
                value={question?.max_value}
              />
              <span className="mr-0.5 text-xs text-gray-400/80">Maximum</span>
            </div>
          </div>
          {errorMessage && <ErrorLabel>{errorMessage}</ErrorLabel>}
        </div>
      ) : null}
    </Space>
  )
}

export default BuilderSideMenuAdvancedSettings
